import React from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Card, Avatar, Badge, Tag, Tooltip } from 'antd';
import { UserOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Appointment } from '../../services/appointmentApi';
import './EmployeeCard.css';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface TechnicianSchedule {
  id: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ScheduleException {
  id: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
}

interface TimeBlock {
  type: 'open' | 'booked' | 'break' | 'unavailable';
  startTime: string;
  endTime: string;
  startMinutes: number;
  endMinutes: number;
  appointment?: Appointment;
  reason?: string;
}

interface EmployeeCardProps {
  employee: Employee;
  date: Dayjs;
  appointments: Appointment[];
  schedule: TechnicianSchedule[];
  exceptions: ScheduleException[];
  onClick: () => void;
}

export const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  date,
  appointments,
  schedule,
  exceptions,
  onClick
}) => {
  const employeeName = `${employee.first_name} ${employee.last_name}`;
  const dayOfWeek = date.day();
  const todaySchedule = schedule.find(s => s.day_of_week === dayOfWeek && s.is_active);

  // Convert time to minutes since midnight
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convert minutes to time string
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  // Calculate time blocks
  const calculateTimeBlocks = (): TimeBlock[] => {
    if (!todaySchedule) return [];

    const blocks: TimeBlock[] = [];
    const startMinutes = timeToMinutes(todaySchedule.start_time);
    const endMinutes = timeToMinutes(todaySchedule.end_time);

    // Sort appointments by start time
    const sortedAppointments = [...appointments]
      .filter(apt => dayjs(apt.start_time).isSame(date, 'day'))
      .sort((a, b) => dayjs(a.start_time).diff(dayjs(b.start_time)));

    // Sort exceptions by start time
    const sortedExceptions = [...exceptions].sort((a, b) =>
      dayjs(a.start_datetime).diff(dayjs(b.start_datetime))
    );

    let currentTime = startMinutes;

    // Process appointments and exceptions together
    const events = [
      ...sortedAppointments.map(apt => ({
        type: 'appointment' as const,
        start: dayjs(apt.start_time).hour() * 60 + dayjs(apt.start_time).minute(),
        end: dayjs(apt.end_time).hour() * 60 + dayjs(apt.end_time).minute(),
        data: apt
      })),
      ...sortedExceptions.map(exc => ({
        type: 'exception' as const,
        start: dayjs(exc.start_datetime).hour() * 60 + dayjs(exc.start_datetime).minute(),
        end: dayjs(exc.end_datetime).hour() * 60 + dayjs(exc.end_datetime).minute(),
        data: exc
      }))
    ].sort((a, b) => a.start - b.start);

    for (const event of events) {
      // Add open time block before this event
      if (currentTime < event.start) {
        blocks.push({
          type: 'open',
          startTime: minutesToTime(currentTime),
          endTime: minutesToTime(event.start),
          startMinutes: currentTime,
          endMinutes: event.start
        });
      }

      // Add event block
      if (event.type === 'appointment') {
        blocks.push({
          type: 'booked',
          startTime: minutesToTime(event.start),
          endTime: minutesToTime(event.end),
          startMinutes: event.start,
          endMinutes: event.end,
          appointment: event.data as Appointment
        });
      } else {
        const exc = event.data as ScheduleException;
        blocks.push({
          type: exc.exception_type === 'break' ? 'break' : 'unavailable',
          startTime: minutesToTime(event.start),
          endTime: minutesToTime(event.end),
          startMinutes: event.start,
          endMinutes: event.end,
          reason: exc.reason || exc.exception_type.replace('_', ' ').toUpperCase()
        });
      }

      currentTime = Math.max(currentTime, event.end);
    }

    // Add final open block if there's time left
    if (currentTime < endMinutes) {
      blocks.push({
        type: 'open',
        startTime: minutesToTime(currentTime),
        endTime: minutesToTime(endMinutes),
        startMinutes: currentTime,
        endMinutes: endMinutes
      });
    }

    return blocks;
  };

  const timeBlocks = calculateTimeBlocks();

  // Calculate stats
  const totalOpenMinutes = timeBlocks
    .filter(b => b.type === 'open')
    .reduce((sum, b) => sum + (b.endMinutes - b.startMinutes), 0);

  const totalBookedMinutes = timeBlocks
    .filter(b => b.type === 'booked')
    .reduce((sum, b) => sum + (b.endMinutes - b.startMinutes), 0);

  const appointmentCount = timeBlocks.filter(b => b.type === 'booked').length;

  // Determine status
  const getStatus = (): { color: string; text: string } => {
    if (!todaySchedule) return { color: 'default', text: 'Off Today' };
    if (totalOpenMinutes === 0) return { color: 'red', text: 'Fully Booked' };
    if (totalOpenMinutes < 60) return { color: 'orange', text: 'Almost Full' };
    if (totalOpenMinutes < 120) return { color: 'yellow', text: 'Busy' };
    return { color: 'green', text: 'Available' };
  };

  const status = getStatus();

  return (
    <Badge.Ribbon text={employee.role.toUpperCase()} color={employee.role === 'owner' ? 'purple' : 'blue'}>
      <Card
        hoverable
        className="employee-availability-card"
        onClick={onClick}
      >
        {/* Employee Header */}
        <div className="employee-card-header">
          <Avatar
            size={56}
            icon={<UserOutlined />}
            style={{
              backgroundColor: status.color === 'green' ? '#52c41a' :
                               status.color === 'yellow' ? '#faad14' :
                               status.color === 'orange' ? '#fa8c16' :
                               status.color === 'red' ? '#ff4d4f' : '#d9d9d9',
              fontWeight: 'bold'
            }}
          />
          <div className="employee-card-info">
            <h3 className="employee-card-name">{employeeName}</h3>
            <Badge status={status.color as any} text={status.text} />
          </div>
        </div>

        {/* Time Blocks */}
        {!todaySchedule ? (
          <div className="off-day-notice">
            <CloseCircleOutlined style={{ fontSize: '24px', color: '#d9d9d9' }} />
            <span>Not scheduled today</span>
          </div>
        ) : (
          <>
            {/* Quick Stats */}
            <div className="time-stats">
              <div className="stat-chip open">
                <CheckCircleOutlined /> {Math.floor(totalOpenMinutes / 60)}h {totalOpenMinutes % 60}m Open
              </div>
              <div className="stat-chip booked">
                <ClockCircleOutlined /> {appointmentCount} Appointment{appointmentCount !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Visual Time Blocks */}
            <div className="time-blocks-container">
              {timeBlocks.map((block, index) => {
                const duration = block.endMinutes - block.startMinutes;
                const durationText = duration >= 60
                  ? `${Math.floor(duration / 60)}h ${duration % 60}m`
                  : `${duration}m`;

                return (
                  <Tooltip
                    key={index}
                    title={
                      block.type === 'booked' && block.appointment
                        ? `${block.appointment.customer?.first_name} ${block.appointment.customer?.last_name} - ${block.appointment.title || block.appointment.appointment_type}`
                        : block.reason || `${block.type.toUpperCase()} - ${durationText}`
                    }
                  >
                    <div className={`time-block ${block.type}`}>
                      <div className="time-block-header">
                        {block.type === 'open' && <CheckCircleOutlined />}
                        {block.type === 'booked' && <ClockCircleOutlined />}
                        {(block.type === 'break' || block.type === 'unavailable') && <CloseCircleOutlined />}
                        <span className="time-block-label">
                          {block.type === 'open' && 'OPEN'}
                          {block.type === 'booked' && 'BOOKED'}
                          {block.type === 'break' && 'BREAK'}
                          {block.type === 'unavailable' && 'OFF'}
                        </span>
                      </div>
                      <div className="time-block-time">
                        {block.startTime} - {block.endTime}
                      </div>
                      {block.type === 'booked' && block.appointment && (
                        <div className="time-block-details">
                          {block.appointment.customer && (
                            <div className="appointment-customer-name">
                              {block.appointment.customer.first_name} {block.appointment.customer.last_name}
                            </div>
                          )}
                          <div className="appointment-service">
                            {block.appointment.title || block.appointment.appointment_type}
                          </div>
                        </div>
                      )}
                      <div className="time-block-duration">
                        {durationText}
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </Badge.Ribbon>
  );
};
